import React from "react";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Button,
  FormControl,
  Select,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

// Sidebar & UI Icons
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  DashboardSidebarIcon,
  ReservationsSidebarIcon,
  HousekeepingSidebarIcon,
  RestaurantSidebarIcon,
  InventorySidebarIcon,
  StaffSidebarIcon,
  UsersSidebarIcon,
  PermissionsSidebarIcon,
  SecuritySidebarIcon,
  HotelProfileSidebarIcon,
  HotelsSidebarIcon,
  BookingChannelsSidebarIcon,
  EmailTemplatesSidebarIcon,
  TaxesFeesSidebarIcon,
  PaymentMethodsSidebarIcon,
  RoomManagementSidebarIcon,
  RatesPricingSidebarIcon,
  FinanceSidebarIcon,
  ReportsSidebarIcon,
  OtaChannelsSidebarIcon,
  NotificationsSidebarIcon,
  LogoutSidebarIcon,
  SteykayLogoIcon,
} from "./SidebarIcons";


import { Badge, Popover, Card, CardHeader, CardContent, Link, Alert } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoIcon from "@mui/icons-material/Info";
import ChangePasswordModal from "./ChangePasswordModal";
import SessionTimeoutWatcher from "./SessionTimeoutWatcher";
import PropertySwitcher from "./PropertySwitcher";
import NightAuditModal from "../pages/Finance/components/NightAuditModal";
import { NotificationService } from "../api/services/notificationService";
import { FinanceService } from "../api/services/financeService";
import { HotelService } from "../api/services/hotelService";
import { ReservationService } from "../api/services/reservationService";
import { extractTotalCount } from "./common/tableStyles";
import { getCookie } from "../utils/cookies";
import { setActiveCurrency } from "../utils/currency";
import { calculateAuditDates } from "../utils/dateUtils";

const drawerWidth = 260;

const normalizeRole = (role) => {
  const roleName = typeof role === "object" && role !== null ? role.name : role;
  return String(roleName || "")
    .replace(/[_\s-]+/g, "")
    .toUpperCase();
};

const formatRoleName = (role) => {
  let roleName = role;
  if (typeof roleName === "object" && roleName !== null) {
    roleName = typeof roleName.name === "object" && roleName.name !== null ? roleName.name.name : roleName.name;
  }
  if (!roleName) return "";
  const str = String(roleName).trim();
  const norm = str.replace(/[_\s-]+/g, "").toUpperCase();

  // Known multi-word roles without spaces/separators
  const specialCases = {
    SUPERADMIN: "Super Admin",
    HOTELADMIN: "Hotel Admin",
    FRONTDESK: "Front Desk",
    CORPORATEADMIN: "Corporate Admin",
    APIUSER: "API User",
  };
  if (specialCases[norm]) return specialCases[norm];

  // Convert camelCase or PascalCase to spaced words (e.g., "FrontDesk" -> "Front Desk")
  const withSpaces = str.replace(/([a-z])([A-Z])/g, "$1 $2");

  // Format snake_case, kebab-case, ALLCAPS, or multiple words
  if (withSpaces.includes("_") || withSpaces.includes("-") || withSpaces === withSpaces.toUpperCase() || withSpaces.includes(" ")) {
    return withSpaces
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  }

  // Single word: capitalize first letter
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// ─── Role-based navigation config ─────────────────────────────────────────────
const ALL_NAV_ITEMS = [
  { title: "Dashboard", path: "/dashboard", icon: <DashboardSidebarIcon />, permissions: ["CAN_VIEW_DASHBOARD"] },
  
  // Section: Operations
  { divider: true, label: "OPERATIONS", permissions: ["CAN_VIEW_BOOKING_CALENDAR", "CAN_VIEW_RESERVATIONS", "CAN_CREATE_BOOKING", "CAN_MONITOR_CLEANLINESS", "CAN_VIEW_HOUSEKEEPING", "CAN_VIEW_HS_TASKS", "CAN_COMPLETE_HS_TASK", "CAN_UPDATE_ROOM_STATUS", "CAN_MODIFY_MENU", "CAN_APPROVE_ORDER", "CAN_VIEW_STOCK_DASHBOARD", "CAN_APPROVE_REQUISITION", "CAN_RAISE_STOCK_REQUEST", "CAN_VIEW_INVENTORY", "CAN_MANAGE_STAFF", "CAN_VIEW_RESTAURANT", "CAN_VIEW_KOT", "CAN_UPDATE_ORDER_STATUS", "IS_STAFF"] },
  { title: "Reservations", path: "/reservations", icon: <ReservationsSidebarIcon />, permissions: ["CAN_VIEW_BOOKING_CALENDAR", "CAN_VIEW_RESERVATIONS", "CAN_CREATE_BOOKING"] },
  { title: "Housekeeping", path: "/housekeeping", icon: <HousekeepingSidebarIcon />, permissions: ["CAN_MONITOR_CLEANLINESS", "CAN_VIEW_HOUSEKEEPING", "CAN_VIEW_HS_TASKS", "CAN_COMPLETE_HS_TASK", "CAN_UPDATE_ROOM_STATUS"] },
  { title: "Restaurant", path: "/restaurant", icon: <RestaurantSidebarIcon />, permissions: ["CAN_MODIFY_MENU", "CAN_APPROVE_ORDER", "CAN_VIEW_RESTAURANT", "CAN_VIEW_KOT", "CAN_UPDATE_ORDER_STATUS"] },
  { title: "Inventory", path: "/inventory", icon: <InventorySidebarIcon />, permissions: ["CAN_VIEW_STOCK_DASHBOARD", "CAN_APPROVE_REQUISITION", "CAN_RAISE_STOCK_REQUEST", "CAN_VIEW_INVENTORY"] },
  { title: "Staff", path: "/staff", icon: <StaffSidebarIcon />, permissions: ["CAN_MANAGE_STAFF", "IS_STAFF"] },

  // Section: Administration
  { divider: true, label: "ADMINISTRATION", permissions: ["CAN_MANAGE_USERS", "CAN_MANAGE_ROLES", "CAN_MANAGE_PERMISSIONS", "CAN_MANAGE_SECURITY", "CAN_MANAGE_HOTELS"] },
  { title: "Users", path: "/users", icon: <UsersSidebarIcon />, permissions: ["CAN_MANAGE_USERS"] },
  { title: "Permissions", path: "/permissions", icon: <PermissionsSidebarIcon />, permissions: ["CAN_MANAGE_ROLES", "CAN_MANAGE_PERMISSIONS"] },
  { title: "Security", path: "/security", icon: <SecuritySidebarIcon />, permissions: ["CAN_MANAGE_SECURITY"] },
  { title: "Hotels", path: "/hotels", icon: <HotelsSidebarIcon />, permissions: ["CAN_MANAGE_HOTELS"] },

  // Section: Property Config
  { divider: true, label: "PROPERTY CONFIG", permissions: ["CAN_MANAGE_SETTINGS", "CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS", "CAN_VIEW_RATES", "CAN_MANAGE_RATES"] },
  { title: "Hotel Profile", path: "/hotel-settings", icon: <HotelProfileSidebarIcon />, permissions: ["CAN_MANAGE_SETTINGS"] },
  { title: "Booking Channels", path: "/hotel-settings/booking-sources", icon: <BookingChannelsSidebarIcon />, permissions: ["CAN_MANAGE_SETTINGS"] },
  { title: "Email Templates", path: "/hotel-settings/email-templates", icon: <EmailTemplatesSidebarIcon />, permissions: ["CAN_MANAGE_SETTINGS"] },
  { title: "Taxes & Fees", path: "/hotel-settings/taxes", icon: <TaxesFeesSidebarIcon />, permissions: ["CAN_MANAGE_SETTINGS"] },
  { title: "Payment Methods", path: "/hotel-settings/payment-methods", icon: <PaymentMethodsSidebarIcon />, permissions: ["CAN_MANAGE_SETTINGS"] },
  { title: "Room Management", path: "/room-management", icon: <RoomManagementSidebarIcon />, permissions: ["CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS"] },
  { title: "Rates & Pricing", path: "/pricing-management", icon: <RatesPricingSidebarIcon />, permissions: ["CAN_VIEW_RATES", "CAN_MANAGE_RATES", "CAN_VIEW_ROOMS", "CAN_MANAGE_ROOMS"] },

  // Section: Finance & Reports
  { divider: true, label: "FINANCE & REPORTS", permissions: ["CAN_VIEW_FINANCIALS", "CAN_APPROVE_EXPENSE", "CAN_VIEW_NIGHT_AUDIT", "CAN_VIEW_REPORTS", "CAN_EXPORT_REPORTS"] },
  { title: "Finance", path: "/finance", icon: <FinanceSidebarIcon />, permissions: ["CAN_VIEW_FINANCIALS", "CAN_APPROVE_EXPENSE", "CAN_VIEW_NIGHT_AUDIT"] },
  { title: "Reports", path: "/reports", icon: <ReportsSidebarIcon />, permissions: ["CAN_VIEW_REPORTS", "CAN_EXPORT_REPORTS"] },

  // Section: Integrations
  { divider: true, label: "INTEGRATIONS", permissions: ["CAN_MANAGE_OTA", "CAN_VIEW_LOGS"] },
  { title: "OTA / Channels", path: "/ota", icon: <OtaChannelsSidebarIcon />, permissions: ["CAN_MANAGE_OTA"] },
  { title: "Notifications", path: "/notifications", icon: <NotificationsSidebarIcon />, permissions: ["CAN_VIEW_LOGS"] },
];

export default function Layout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, tenants } = useSelector((state) => state.auth);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [anchorElNotify, setAnchorElNotify] = React.useState(null);
  const [changePwdOpen, setChangePwdOpen] = React.useState(false);
  const [pwdLoading, setPwdLoading] = React.useState(false);
  const [pwdError, setPwdError] = React.useState(null);
  const [pwdSuccess, setPwdSuccess] = React.useState(false);

  // Notification states
  const [notifications, setNotifications] = React.useState([]);
  const [notifyLoading, setNotifyLoading] = React.useState(false);
  const [notifyError, setNotifyError] = React.useState(null);
  const [isMarkingRead, setIsMarkingRead] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [nightAuditStatus, setNightAuditStatus] = React.useState(null);
  const [auditModalOpen, setAuditModalOpen] = React.useState(false);
  const [reservationsCount, setReservationsCount] = React.useState(null);

  const authData = React.useMemo(() => {
    try {
      return JSON.parse(getCookie("adminAuth") || localStorage.getItem("adminAuth") || "{}");
    } catch {
      return {};
    }
  }, []);

  const normalizedRole = normalizeRole(user?.role || authData?.role);
  const isSuperAdmin = normalizedRole === "SUPERADMIN";

  const rawRole =
    (typeof user?.role === "object" && user?.role !== null ? user.role.name : user?.role) ||
    (typeof profile?.role === "object" && profile?.role !== null ? profile.role.name : profile?.role) ||
    (typeof authData?.role === "object" && authData?.role !== null ? authData.role.name : authData?.role) ||
    (isSuperAdmin ? "Super Admin" : "");

  const displayRole = formatRoleName(rawRole);
  const displayName = user?.name || profile?.name || authData?.name || "Admin User";

  // Fetch tenants, unread count and register push on mount
  React.useEffect(() => {
    dispatch.auth.fetchTenantsAsync();
    fetchUnreadCount();
    setupPushNotifications();
  }, [dispatch]);

  // Fetch live reservations count for sidebar badge
  const fetchReservationsCount = React.useCallback(async () => {
    try {
      const authData = JSON.parse(getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}');
      const numericTenantId = authData.tenantId || user?.tenantId;
      if (!numericTenantId || numericTenantId === 'null') {
        setReservationsCount(null);
        return;
      }
      const res = await ReservationService.getAll({ page: 1, limit: 10 }, numericTenantId);
      const list = res?.data || res?.reservations || (Array.isArray(res) ? res : []);
      const total = extractTotalCount(res, list);
      setReservationsCount(total);
    } catch (err) {
      console.warn("Failed to fetch reservations count for sidebar badge:", err);
    }
  }, [user?.tenantId]);

  React.useEffect(() => {
    fetchReservationsCount();
    window.addEventListener('reservationUpdated', fetchReservationsCount);
    window.addEventListener('focus', fetchReservationsCount);
    return () => {
      window.removeEventListener('reservationUpdated', fetchReservationsCount);
      window.removeEventListener('focus', fetchReservationsCount);
    };
  }, [fetchReservationsCount, tenants, location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotifyOpen = (event) => {
    setAnchorElNotify(event.currentTarget);
    fetchNotifications();
  };
  const handleNotifyClose = () => {
    setAnchorElNotify(null);
  };

  const fetchNotifications = async () => {
    setNotifyLoading(true);
    setNotifyError(null);
    try {
      const authData = JSON.parse(getCookie('adminAuth') || '{}');
      const tenantId = authData.tenantId || null;
      const data = await NotificationService.getInboxNotifications(tenantId);
      const list = Array.isArray(data) ? data : [];
      setNotifications(list);
      // Sync unread count with the full list
      setUnreadCount(list.filter(n => n.status !== 'Read').length);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      setNotifyError('Could not load notifications');
    } finally {
      setNotifyLoading(false);
    }
  };

  const setupPushNotifications = async () => {
    try {
      if (!("Notification" in window)) return;
      
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        // In a real scenario, you'd get the token from FCM or a Service Worker
        // For now, we'll use a placeholder or simulate token generation
        const token = await getDeviceToken();
        if (token) {
          const authData = JSON.parse(getCookie('adminAuth') || '{}');
          const tenantId = authData.tenantId || null;
          
          await NotificationService.registerPushToken({
            token: token,
            deviceType: getDeviceType()
          }, tenantId);
          
          localStorage.setItem('pushToken', token);
        }
      }
    } catch (err) {
      // Fail silently as requested
      console.warn('Push registration failed:', err);
    }
  };

  const getDeviceToken = async () => {
    // This would typically involve navigator.serviceWorker.ready and pushManager.subscribe
    // Or FCM getToken(). We'll simulate a token for this implementation.
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription() || 
                           await registration.pushManager.subscribe({
                             userVisibleOnly: true,
                             applicationServerKey: 'BEl62zl9jyq-8C97W79S9j_5S5-mG-uE' // Placeholder
                           });
      return subscription.endpoint;
    } catch (e) {
      return 'simulated-web-token-' + Math.random().toString(36).substr(2, 9);
    }
  };

  const getDeviceType = () => {
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) return 'ios';
    if (/android/.test(ua)) return 'android';
    return 'web';
  };

  const fetchUnreadCount = async () => {
    try {
      const authData = JSON.parse(getCookie('adminAuth') || '{}');
      const tenantId = authData.tenantId || null;
      const count = await NotificationService.getUnreadCount(tenantId);
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };

  const handleMarkAllRead = async () => {
    if (notifications.length === 0 || isMarkingRead) return;

    // Optimistic UI update
    const previousNotifications = [...notifications];
    const previousUnreadCount = unreadCount;
    setNotifications(notifications.map(n => ({ ...n, status: 'Read' })));
    setUnreadCount(0);

    setIsMarkingRead(true);
    try {
      const authData = JSON.parse(getCookie('adminAuth') || '{}');
      const tenantId = authData.tenantId || null;
      await NotificationService.markAllRead(tenantId);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      // Revert optimistic update on failure
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
      setNotifyError('Failed to mark notifications as read');
    } finally {
      setIsMarkingRead(false);
    }
  };

  const effectivePermissions = React.useMemo(() => {
    if (!user) return [];
    
    let rawPerms = [];
    if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
      rawPerms = user.customPermissions;
    } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
      rawPerms = user.permissions;
    } else if (user.role && Array.isArray(user.role.permissions)) {
      rawPerms = user.role.permissions;
    }

    const perms = rawPerms.map(p => {
      if (typeof p === 'string') return p;
      if (p && typeof p === 'object') {
        return p.key || p.name || p.code || '';
      }
      return '';
    }).filter(Boolean);
    
    if (user?.isStaff) {
        perms.push("IS_STAFF");
    }
    
    return perms;
  }, [user]);

  React.useEffect(() => {
    // Check if user has Night Audit permissions
    const hasPerm = effectivePermissions.includes("*") || effectivePermissions.includes("CAN_VIEW_NIGHT_AUDIT") || effectivePermissions.includes("CAN_RUN_NIGHT_AUDIT");
    
    const checkStatus = () => {
        if (hasPerm && user?.tenantId && tenants.length > 0) {
            checkNightAuditStatus();
        }
    };

    checkStatus();

    window.addEventListener('nightAuditCompleted', checkStatus);
    return () => {
        window.removeEventListener('nightAuditCompleted', checkStatus);
    };
  }, [effectivePermissions, user?.tenantId, tenants]);

  // Sync active property currency across admin app
  React.useEffect(() => {
    const syncActiveCurrency = async () => {
      try {
        const authData = JSON.parse(getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}');
        const numericTenantId = authData.tenantId || user?.tenantId;
        if (!numericTenantId || numericTenantId === 'null') return;

        const activeTenant = tenants?.find(t => 
          String(t.tenantId || t.id || t._id) === String(numericTenantId) ||
          String(t.id || t._id) === String(authData.hotelId)
        );
        if (activeTenant?.currency) {
          setActiveCurrency(activeTenant.currency);
          return;
        }
        const hotelUuid = activeTenant?.id || activeTenant?._id || authData.hotelId;
        if (hotelUuid) {
          const res = await HotelService.getById(hotelUuid, numericTenantId);
          const hotel = res?.data || res;
          if (hotel?.currency) {
            setActiveCurrency(hotel.currency);
          }
        }
      } catch (_) {}
    };

    syncActiveCurrency();
  }, [user?.tenantId, tenants]);

  const checkNightAuditStatus = async () => {
    try {
        const authData = JSON.parse(getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}');
        const numericTenantId = authData.tenantId || null;
        
        // The API requires the hotel UUID in the path, but the numeric tenantId in the header
        const activeTenant = (tenants || []).find(t => 
          String(t.tenantId || t.id || t._id) === String(numericTenantId) ||
          String(t.id || t._id) === String(authData.hotelId)
        );
        const hotelUuid = activeTenant?.id || activeTenant?._id || authData.hotelId;
        if (!hotelUuid) return;
        
        const res = await HotelService.getById(hotelUuid, numericTenantId);
        const hotel = res?.data || res;
        
        if (activeTenant?.currency) {
            setActiveCurrency(activeTenant.currency);
        }
        if (hotel?.currency) {
            setActiveCurrency(hotel.currency);
        }

        if (hotel) {
            let lastClosedInput = null;
            try {
                const historyRes = await FinanceService.getNightAuditHistory({ page: 0, limit: 5 });
                const historyList = Array.isArray(historyRes?.data) ? historyRes.data : (Array.isArray(historyRes) ? historyRes : []);
                const latestCompleted = historyList.find(r => ['Completed', 'Balanced', 'Success'].includes(r.status));
                if (latestCompleted) {
                    lastClosedInput = latestCompleted.date || latestCompleted.createdAt;
                }
            } catch (err) {
                console.warn("Could not fetch night audit history in Layout", err);
            }

            const auditCalc = calculateAuditDates(lastClosedInput, hotel.businessDate || hotel.createdAt);
            
            if (auditCalc.diffDays > 0) {
                setNightAuditStatus({
                    behindDays: auditCalc.diffDays,
                    missedDate: auditCalc.pendingDateFormatted,
                    lastClosedDate: auditCalc.lastClosedFormatted
                });
            } else {
                setNightAuditStatus(null);
            }
        }
    } catch (err) {
        console.error("Failed to fetch night audit status", err);
    }
  };

  // Filter nav items by permissions and selected tenant context
  const filteredNavItems = React.useMemo(() => {
    return ALL_NAV_ITEMS.map((item) => {
      if (item.path === "/reservations") {
        return {
          ...item,
          badge: typeof reservationsCount === "number" && reservationsCount > 0 ? reservationsCount : undefined,
        };
      }
      return item;
    }).filter((item) => {
      const hasNoTenant = !user?.tenantId || user?.tenantId === 'null';

      if (hasNoTenant) {
        // Global "All Properties" context:
        // Only show Users, Permissions, Security, Hotels, and the ADMINISTRATION divider (no Dashboard)
        if (item.divider) {
          return item.label === "ADMINISTRATION";
        }
        const allowedPaths = ["/users", "/permissions", "/security", "/hotels"];
        if (!allowedPaths.includes(item.path)) {
          return false;
        }
      } else {
        // Specific Tenant context:
        // Hide the global Hotels management menu item
        if (item.path === "/hotels") {
          return false;
        }
      }

      if (!item.permissions || item.permissions.length === 0) return true;
      // The "*" wildcard grants access to all permissions
      if (effectivePermissions.includes("*")) return true;
      return item.permissions.some(perm => effectivePermissions.includes(perm));
    });
  }, [user?.tenantId, effectivePermissions, reservationsCount]); // Memoize to prevent infinite loops

  // Enforce Context Routing: If the user navigates to a URL that is hidden from their sidebar 
  // (due to tenant context or permissions), redirect them to the dashboard immediately.
  React.useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/' || currentPath === '/dashboard') return;

    const isAllowed = filteredNavItems.some(item => 
      !item.divider && (currentPath === item.path || currentPath.startsWith(item.path + '/'))
    );

    if (!isAllowed) {
      navigate('/dashboard', { replace: true });
    }
  }, [location.pathname, filteredNavItems, navigate]);

  const handleLogout = async () => {
    try {
      const pushToken = localStorage.getItem('pushToken');
      if (pushToken) {
        const authData = JSON.parse(getCookie('adminAuth') || '{}');
        const tenantId = authData.tenantId || null;
        await NotificationService.unregisterPushToken({ token: pushToken }, tenantId);
      }
    } catch (err) {
      console.warn('Failed to unregister push token during logout:', err);
    } finally {
      localStorage.removeItem('pushToken');
      await dispatch.auth.logoutAsync();
      navigate("/login");
    }
  };



  // Add handler for password change
  const handleChangePassword = async ({ oldPassword, newPassword }) => {
    setPwdLoading(true);
    setPwdError(null);
    setPwdSuccess(false);
    const result = await dispatch.auth.changePasswordAsync({
      oldPassword,
      newPassword,
    });
    if (result.success) {
      setPwdSuccess(true);
    } else {
      setPwdError(result.message);
    }
    setPwdLoading(false);
  };

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#141425",
        color: "#ffffff",
      }}
    >
      {/* Brand Header matching reference image */}
      <Box
        sx={{
          px: 2.5,
          py: 2.2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <SteykayLogoIcon size={40} />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "1.2rem",
              lineHeight: 1.15,
              letterSpacing: "-0.25px",
            }}
          >
            <Box component="span" sx={{ color: "#ffffff", fontWeight: 800 }}>
              Stey
            </Box>
            <Box component="span" sx={{ color: "#7a8cf4", fontWeight: 800 }}>
              kay
            </Box>
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: "#8391a4",
              fontSize: "0.66rem",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              lineHeight: 1.1,
              mt: 0.35,
            }}
          >
            ENTERPRISE SUITE
          </Typography>
        </Box>
      </Box>

      {/* Navigation List */}
      <List
        sx={{
          flexGrow: 1,
          px: 1.5,
          pt: 0.5,
          pb: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255, 255, 255, 0.12)",
            borderRadius: "10px",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255, 255, 255, 0.22)",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255, 255, 255, 0.12) transparent",
        }}
      >
        {filteredNavItems.map((item, idx) => {
          // Render section divider / category header
          if (item.divider) {
            return (
              <Typography
                key={`divider-${idx}`}
                variant="overline"
                sx={{
                  display: "block",
                  color: "#5e657e",
                  mt: idx === 0 ? 0.5 : 2.2,
                  mb: 0.6,
                  px: 1.5,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  lineHeight: 1.4,
                }}
              >
                {item.label}
              </Typography>
            );
          }

          const active =
            location.pathname === item.path ||
            (item.path !== "/" &&
              item.path !== "/hotel-settings" &&
              location.pathname.startsWith(item.path + "/"));

          return (
            <ListItem key={item.title} disablePadding sx={{ mb: 0.4 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: "8px",
                  py: 0.85,
                  px: 1.5,
                  minHeight: "40px",
                  bgcolor: active ? "#4146e0" : "transparent",
                  boxShadow: active ? "0 4px 14px rgba(65, 70, 224, 0.35)" : "none",
                  transition: "all 0.15s ease-in-out",
                  "&:hover": {
                    bgcolor: active ? "#4358ff" : "rgba(255,255,255,0.05)",
                    "& .MuiListItemIcon-root": {
                      color: active ? "#ffffff" : "#ffffff",
                    },
                    "& .MuiListItemText-primary": {
                      color: active ? "#ffffff" : "#ffffff",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: active ? "#ffffff" : "#8f95b2",
                    minWidth: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    transition: "color 0.15s ease-in-out",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  primaryTypographyProps={{
                    fontWeight: active ? 600 : 500,
                    color: active ? "#ffffff" : "#8f95b2",
                    fontSize: "0.85rem",
                    lineHeight: 1.2,
                    transition: "color 0.15s ease-in-out",
                  }}
                />
                {item.badge && (
                  <Box
                    sx={{
                      bgcolor: "#4146e0",
                      color: "#ffffff",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      px: 1,
                      py: 0.15,
                      borderRadius: "10px",
                      minWidth: 24,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      lineHeight: 1,
                      ml: 1,
                      flexShrink: 0,
                    }}
                  >
                    {item.badge}
                  </Box>
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Logout Session Button matching reference image */}
      <Box
        sx={{
          p: 1.5,
          borderTop: "1px solid rgba(255, 255, 255, 0.05)",
        }}
      >
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: "8px",
            py: 0.85,
            px: 1.5,
            minHeight: "40px",
            color: "#e05d76",
            transition: "all 0.15s ease-in-out",
            "&:hover": {
              bgcolor: "rgba(224, 93, 118, 0.08)",
              color: "#ff6b87",
              "& .MuiListItemIcon-root": {
                color: "#ff6b87",
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              color: "#e05d76",
              minWidth: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              transition: "color 0.15s ease-in-out",
            }}
          >
            <LogoutSidebarIcon size={18} />
          </ListItemIcon>
          <ListItemText
            primary="Logout Session"
            primaryTypographyProps={{
              fontWeight: 600,
              fontSize: "0.85rem",
              lineHeight: 1.2,
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: "#ffffff",
          color: "text.primary",
          boxShadow: "none",
          borderBottom: "1px solid",
          borderColor: "#eaecf0",
        }}
      >
        <Toolbar sx={{ minHeight: "64px", px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Property Switcher positioned on the left */}
          <PropertySwitcher />

          <Box sx={{ flexGrow: 1 }} />

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {/* Temporarily hidden: Notification Bell (kept hidden as requested) */}
            {false && (
              <IconButton color="inherit" onClick={handleNotifyOpen}>
                <Badge 
                  badgeContent={unreadCount} 
                  color="error"
                  invisible={unreadCount === 0}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}

            <IconButton
              onClick={handleMenuOpen}
              sx={{
                p: 0.5,
                borderRadius: 2,
                "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 0.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    textAlign: "left",
                  }}
                >
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1e293b",
                      fontSize: "0.875rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {displayName}
                  </Typography>
                  {displayRole && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: "#64748b",
                        fontSize: "0.72rem",
                        fontWeight: 500,
                        lineHeight: 1.2,
                        mt: 0.25,
                      }}
                    >
                      {displayRole}
                    </Typography>
                  )}
                </Box>
                <Avatar
                  sx={{
                    bgcolor: "#009688",
                    width: 36,
                    height: 36,
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                >
                  {displayName.charAt(0) || "A"}
                </Avatar>
              </Box>
            </IconButton>

            {/* Temporarily hidden: Notification Popover */}
            {false && (
              <Popover
                open={Boolean(anchorElNotify)}
                anchorEl={anchorElNotify}
                onClose={handleNotifyClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    width: 360,
                    mt: 1,
                    borderRadius: 3,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Card elevation={0}>
                  <CardHeader
                    title="Notifications"
                    titleTypographyProps={{
                      variant: "subtitle1",
                      fontWeight: "bold",
                    }}
                    action={
                      (notifications.filter(n => n.status !== 'Read').length > 0 && !notifyLoading && !notifyError) ? (
                        <Button 
                          size="small" 
                          sx={{ textTransform: "none" }}
                          onClick={handleMarkAllRead}
                          disabled={isMarkingRead}
                          startIcon={isMarkingRead && <CircularProgress size={12} />}
                        >
                          {isMarkingRead ? 'Marking...' : 'Mark all as read'}
                        </Button>
                      ) : null
                    }
                    sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}
                  />
                  <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                    {notifyLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : notifyError ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="error">{notifyError}</Typography>
                      </Box>
                    ) : (
                      <List sx={{ py: 0 }}>
                        {notifications.length > 0 ? (
                          notifications.slice(0, 5).map((n) => (
                            <ListItemButton
                              key={n.id}
                              onClick={() => {
                                handleNotifyClose();
                                if (n.link) navigate(n.link);
                              }}
                              sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                bgcolor: n.status === 'Read'
                                  ? "transparent"
                                  : "rgba(25, 118, 210, 0.04)",
                                py: 2,
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }}>
                                {n.type === "Critical" ? (
                                  <ErrorOutlineIcon color="error" />
                                ) : n.type === "Warning" ? (
                                  <WarningAmberIcon color="warning" />
                                ) : (
                                  <InfoIcon color="primary" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography
                                    variant="body2"
                                    fontWeight={n.status === 'Read' ? "medium" : "bold"}
                                  >
                                    {n.title}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block" }}
                                    >
                                      {n.message}
                                    </Typography>
                                    <Typography variant="caption" color="text.disabled">
                                      {n.timestamp ? new Date(n.timestamp).toLocaleTimeString() : ''}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItemButton>
                          ))
                        ) : (
                          <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              No new notifications
                            </Typography>
                          </Box>
                        )}
                      </List>
                    )}
                    {(notifications.length > 2 && !notifyLoading && !notifyError) && (
                      <Box sx={{ p: 1.5, textAlign: "center" }}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => {
                            handleNotifyClose();
                            navigate("/notifications");
                          }}
                          sx={{ fontWeight: "bold", textDecoration: "none" }}
                        >
                          View All Notifications
                        </Link>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Popover>
            )}

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  borderRadius: 2,
                  minWidth: 150,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setChangePwdOpen(true);
                }}
                sx={{ py: 1.5 }}
              >
                <Typography variant="body2" fontWeight="medium">
                  Change Password
                </Typography>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleLogout();
                }}
                sx={{ color: "error.main", py: 1.5 }}
              >
                <ListItemIcon sx={{ color: "error.main", minWidth: 35 }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="body2" fontWeight="medium">
                  Logout
                </Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "#141425",
              borderRight: "1px solid rgba(255, 255, 255, 0.05)",
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              bgcolor: "#141425",
              borderRight: "1px solid rgba(255, 255, 255, 0.05)",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: "64px",
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Box sx={{ p: 3, flexGrow: 1 }}>
            {/* Global Night Audit Pending Banner Matching Reference Design */}
            {nightAuditStatus && !location.pathname.startsWith('/finance') && (
                <Box sx={{
                    bgcolor: '#fffbeb',
                    border: '1px solid #fef3c7',
                    borderRadius: 2,
                    py: 1.25,
                    px: 2.5,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#d97706' }} />
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#78350f' }}>
                            <strong style={{ fontWeight: 700 }}>Night Audit Pending:</strong> {nightAuditStatus?.missedDate ? `${nightAuditStatus.missedDate} pending closure. Ledgers remain provisional.` : 'July 31 pending closure. Ledgers remain provisional.'}
                        </Typography>
                    </Box>
                    <Typography
                        variant="body2"
                        onClick={() => setAuditModalOpen(true)}
                        sx={{
                            color: '#4f46e5',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': { color: '#3730a3' }
                        }}
                    >
                        Execute Process
                    </Typography>
                </Box>
            )}
            <Outlet />
        </Box>
      </Box>
      <ChangePasswordModal
        open={changePwdOpen}
        onClose={() => {
          setChangePwdOpen(false);
          setPwdError(null);
          setPwdSuccess(false);
        }}
        onSubmit={handleChangePassword}
        loading={pwdLoading}
        error={pwdError}
        success={pwdSuccess}
      />
      <NightAuditModal
        open={auditModalOpen}
        onClose={() => setAuditModalOpen(false)}
        onSuccess={() => {
          setAuditModalOpen(false);
          setNightAuditStatus(null);
          window.dispatchEvent(new Event('nightAuditCompleted'));
        }}
      />
      <SessionTimeoutWatcher />
    </Box>
  );
}
